// ─── Control de órdenes: la OP es el documento central ──────────────────────
// Todo lo que se ejecuta para un cliente cuelga de su orden de producción: de
// ella salen las compras (OCP), los compromisos con terceros (cuentas de cobro
// y sus OCI), las entregas (remisiones) y, al final, la factura — y la orden no
// está terminada hasta que esa factura está COBRADA y lo que se le debe a
// proveedores y terceros está pagado. Este tablero mira cada orden por ese
// hilo completo y dice en qué paso va, con el dinero que falta en cada uno.
//
// No inventa ninguna cifra: cada columna sale de la misma puerta que ya usa el
// módulo correspondiente (`balanceFacturacionOP` para lo pedido/entregado/
// facturado, `getSaldoFactura` para lo que falta por cobrar, `getSaldoOCP` y
// las CxP para lo que falta por pagar, `getLiquidacionOP` para el margen).
import { C, ESTADOS, ESTADO_COLOR } from "../core/config.js";
import { desgloseIVA } from "../core/iva.js";
import { state } from "../core/state.js";
import { btnStyle, escapeHTML, fmtCOP, fmtDate, normalize, parseNumber, searchBox, today } from "../core/utils.js";
import { registerActions } from "../ui/actions.js";
import { render } from "../ui/app.js";
import { balanceFacturacionOP, facturaCubreOP, getLiquidacionOP, getSaldoOCP, remisionesDeOP } from "./documentos.js";
import { getSaldoFactura } from "./facturas.js";
import { esOcpInformativa, ocpTotalmenteIngresada } from "./cxp.js";
import { netoDe } from "./cuentasPagar.js";

// Los pasos de la venta, en el orden del trabajo. La etapa de una orden es el
// PRIMERO que falta: no se puede facturar lo que no se entregó ni cobrar lo
// que no se facturó.
export const ETAPAS = {
  por_entregar: { label: "📦 Por entregar", color: "#b45309", bg: "#fef3c7" },
  por_facturar: { label: "🧾 Por facturar", color: "#1d4ed8", bg: "#dbeafe" },
  por_cobrar:   { label: "💰 Por cobrar",   color: "#0e7490", bg: "#cffafe" },
  por_pagar:    { label: "💸 Falta pagar",  color: "#7c3aed", bg: "#ede9fe" },
  cerrada:      { label: "✅ Cerrada",      color: "#047857", bg: "#d1fae5" },
  cancelada:    { label: "❌ Cancelada",    color: "#991b1b", bg: "#fee2e2" }
};

// Valor con IVA de unas cantidades a los precios de la orden, línea por línea
// (un bien excluido no causa impuesto tampoco aquí).
function valorLineas(lineas, cantidadDe) {
  const items = lineas.map(l => {
    const cant = cantidadDe(l);
    return { articulo: l.articulo, cantidad: cant, precio_unitario: l.precio_unitario, total: cant * l.precio_unitario };
  }).filter(it => it.cantidad > 0);
  return items.length ? desgloseIVA(items).total : 0;
}

// ─── El hilo completo de una orden, en números ──────────────────────────────
export function seguimientoOP(op) {
  const lineas = balanceFacturacionOP(op);
  const pedidoUds = lineas.reduce((s, l) => s + l.pedido, 0);
  const entregadoUds = lineas.reduce((s, l) => s + Math.min(l.remisionado, l.pedido), 0);
  const facturadoUds = lineas.reduce((s, l) => s + Math.min(l.facturado, l.pedido), 0);
  const porEntregar = valorLineas(lineas, l => Math.max(0, l.pedido - l.remisionado));
  const porFacturar = valorLineas(lineas, l => Math.max(0, l.pedido - l.facturado));

  const remisiones = remisionesDeOP(op.id);
  const facturas = (state.data.facturas || []).filter(f => f.estado !== "anulada" && facturaCubreOP(f, op.id));
  const hoy = today();
  let saldoCobro = 0, vencidas = 0;
  facturas.forEach(f => {
    const saldo = getSaldoFactura(f);
    saldoCobro += saldo;
    if (saldo > 0.5 && f.fecha_vencimiento && f.fecha_vencimiento < hoy) vencidas++;
  });
  const facturasPagadas = facturas.filter(f => getSaldoFactura(f) <= 0.5).length;

  // Compras: las oficiales se ingresan, se causan y se pagan; las informativas
  // (OCI) solo se cierran cuando el proveedor cumple.
  const ocps = (state.data.ocp || []).filter(o => o._origen && o._origen.id === op.id && o.estado !== "cancelada");
  const oficiales = ocps.filter(o => !esOcpInformativa(o));
  const informativas = ocps.filter(o => esOcpInformativa(o));
  const ocpRecibidas = oficiales.filter(o => o.estado === "recibida" || ocpTotalmenteIngresada(o)).length;
  const ocpPagadas = oficiales.filter(o => o.estado_pago === "pagada").length;
  const saldoOcp = oficiales.reduce((s, o) => s + (o.estado_pago === "pagada" ? 0 : getSaldoOCP(o)), 0);
  const ociAbiertas = informativas.filter(o => !o._cerrada).length;

  // Terceros: las cuentas por pagar directas, que se legalizan en cuenta de cobro.
  const cxps = (state.data.cuentas_pagar || []).filter(x => x._origen && x._origen.id === op.id);
  const cxpPagadas = cxps.filter(x => x.estado === "pagada").length;
  const cxpEnCC = cxps.filter(x => x.estado === "en_cuenta_cobro").length;
  const saldoCxp = cxps.reduce((s, x) => s + (x.estado === "pagada" ? 0 : netoDe(x)), 0);

  const liq = getLiquidacionOP(op.id);

  const proveedoresOk = saldoOcp <= 0.5 && saldoCxp <= 0.5 && ociAbiertas === 0
    && oficiales.every(o => o.estado_pago === "pagada");

  let etapa;
  if (op.estado === "cancelada") etapa = "cancelada";
  else if (pedidoUds > 0 && entregadoUds + 0.001 < pedidoUds) etapa = "por_entregar";
  else if (pedidoUds > 0 && facturadoUds + 0.001 < pedidoUds) etapa = "por_facturar";
  else if (saldoCobro > 0.5) etapa = "por_cobrar";
  else if (!proveedoresOk) etapa = "por_pagar";
  else etapa = "cerrada";

  return {
    op, etapa, lineas,
    pedidoUds, entregadoUds, facturadoUds, porEntregar, porFacturar,
    remisiones: remisiones.length,
    facturas, facturasPagadas, saldoCobro, vencidas,
    oficiales: oficiales.length, ocpRecibidas, ocpPagadas, saldoOcp,
    informativas: informativas.length, ociAbiertas,
    cxps: cxps.length, cxpPagadas, cxpEnCC, saldoCxp,
    proveedoresOk,
    margen: liq ? liq.margen : null, margenInicial: liq ? liq.margenInicial : null,
    utilidad: liq ? liq.utilidad : null, venta: parseNumber(op.total) || 0
  };
}

const FILTROS = [
  ["abiertas", "Abiertas"],
  ["por_entregar", "Por entregar"],
  ["por_facturar", "Por facturar"],
  ["por_cobrar", "Por cobrar"],
  ["por_pagar", "Falta pagar"],
  ["cerradas", "Cerradas"],
  ["todas", "Todas"]
];

function pasaFiltro(s, filtro) {
  if (filtro === "todas") return true;
  if (filtro === "abiertas") return s.etapa !== "cerrada" && s.etapa !== "cancelada";
  if (filtro === "cerradas") return s.etapa === "cerrada";
  return s.etapa === filtro;
}

const barra = (hecho, total, color) => {
  const pct = total > 0 ? Math.min(100, Math.round(hecho / total * 100)) : 0;
  return `
    <div style="height:6px;background:#e2e8f0;border-radius:3px;overflow:hidden;margin-top:3px;">
      <div style="width:${pct}%;height:100%;background:${color};"></div>
    </div>`;
};

const chipEtapa = (etapa) => {
  const e = ETAPAS[etapa] || ETAPAS.por_entregar;
  return `<span style="background:${e.bg};color:${e.color};padding:3px 9px;border-radius:6px;font-size:10.5px;font-weight:700;white-space:nowrap;">${e.label}</span>`;
};

export function renderControlOP() {
  if (!state.controlOP) state.controlOP = { filtro: "abiertas" };
  const filtro = state.controlOP.filtro || "abiertas";
  const q = normalize(state.searches.controlop || "");
  const clientes = state.data.clientes || [];
  const nombreCliente = (id) => (clientes.find(c => c.id === id) || {}).nombre || "—";

  // Los borradores todavía no son trabajo comprometido: se ven solo en "Todas".
  const seguimientos = (state.data.pedidos || [])
    .filter(op => filtro === "todas" || op.estado !== "borrador")
    .map(seguimientoOP)
    .filter(s => pasaFiltro(s, filtro))
    .filter(s => !q || normalize(s.op.numero).includes(q) || normalize(nombreCliente(s.op.cliente_id)).includes(q))
    .sort((a, b) => (b.op.fecha || "").localeCompare(a.op.fecha || "") || (b.op.numero || "").localeCompare(a.op.numero || ""));

  // Los totales de arriba son de las órdenes ABIERTAS, sea cual sea el filtro:
  // es la plata que falta por mover.
  const abiertas = (state.data.pedidos || []).filter(op => op.estado !== "borrador").map(seguimientoOP)
    .filter(s => s.etapa !== "cerrada" && s.etapa !== "cancelada");
  const kpi = {
    abiertas: abiertas.length,
    porEntregar: abiertas.reduce((s, x) => s + x.porEntregar, 0),
    porFacturar: abiertas.reduce((s, x) => s + x.porFacturar, 0),
    porCobrar: abiertas.reduce((s, x) => s + x.saldoCobro, 0),
    porPagar: abiertas.reduce((s, x) => s + x.saldoOcp + x.saldoCxp, 0),
    vencidas: abiertas.reduce((s, x) => s + x.vencidas, 0)
  };
  const cuentaPor = {};
  (state.data.pedidos || []).filter(op => op.estado !== "borrador").map(seguimientoOP)
    .forEach(s => { cuentaPor[s.etapa] = (cuentaPor[s.etapa] || 0) + 1; });

  const ordenes = (n) => `${n} ${n === 1 ? "orden" : "órdenes"}`;
  const tarjeta = (titulo, valor, color, pie) => `
    <div style="background:white;border:1px solid ${C.border};border-left:4px solid ${color};border-radius:6px;padding:10px 12px;">
      <div style="font-size:10px;color:${C.muted};font-weight:700;letter-spacing:.4px;">${titulo}</div>
      <div style="font-size:16px;font-weight:800;color:${color};margin-top:2px;">${valor}</div>
      ${pie ? `<div style="font-size:10px;color:${C.muted};margin-top:2px;">${pie}</div>` : ""}
    </div>`;

  const filas = seguimientos.map(s => {
    const op = s.op;
    const est = (ESTADOS.OP && ESTADOS.OP[op.estado]) || op.estado || "";
    const colorEst = ESTADO_COLOR[op.estado] || C.muted;
    const margenColor = s.margen == null ? C.muted : (s.margen >= 30 ? "#10b981" : (s.margen >= 10 ? "#f59e0b" : "#ef4444"));
    const proveedores = [];
    if (s.oficiales) proveedores.push(`🛒 ${s.oficiales} OCP · ${s.ocpRecibidas} recib. · ${s.ocpPagadas} pag.`);
    if (s.informativas) proveedores.push(`📋 ${s.informativas} OCI · ${s.informativas - s.ociAbiertas} cerradas`);
    if (s.cxps) proveedores.push(`🧾 ${s.cxps} CxP · ${s.cxpEnCC} en cta. cobro · ${s.cxpPagadas} pag.`);
    const saldoProv = s.saldoOcp + s.saldoCxp;
    return `
      <tr style="border-top:1px solid ${C.border};background:white;vertical-align:top;">
        <td style="padding:8px 10px;white-space:nowrap;">
          <div style="font-weight:800;color:#2B4A82;font-size:12.5px;">${escapeHTML(op.numero || "")}</div>
          <div style="font-size:10.5px;color:${C.muted};">${fmtDate(op.fecha)}</div>
          <span style="background:${colorEst}22;color:${colorEst};padding:2px 7px;border-radius:6px;font-size:9.5px;font-weight:700;">${est}</span>
        </td>
        <td style="padding:8px 10px;min-width:150px;">
          <div style="font-weight:700;color:${C.text};font-size:11.5px;">${escapeHTML(nombreCliente(op.cliente_id))}</div>
          <div style="font-size:10px;color:${C.muted};">Venta: <b style="color:${C.text};">${fmtCOP(s.venta)}</b></div>
        </td>
        <td style="padding:8px 10px;min-width:130px;">
          <div style="font-size:11px;color:${C.text};"><b>${s.entregadoUds}</b> / ${s.pedidoUds} uds · ${s.remisiones} RS</div>
          ${barra(s.entregadoUds, s.pedidoUds, "#b45309")}
          ${s.porEntregar > 0.5 ? `<div style="font-size:10px;color:#b45309;margin-top:2px;">Faltan ${fmtCOP(s.porEntregar)}</div>` : `<div style="font-size:10px;color:#047857;margin-top:2px;">Entregado</div>`}
        </td>
        <td style="padding:8px 10px;min-width:130px;">
          <div style="font-size:11px;color:${C.text};"><b>${s.facturadoUds}</b> / ${s.pedidoUds} uds · ${s.facturas.length} fact.</div>
          ${barra(s.facturadoUds, s.pedidoUds, "#1d4ed8")}
          ${s.porFacturar > 0.5 ? `<div style="font-size:10px;color:#1d4ed8;margin-top:2px;">Por facturar ${fmtCOP(s.porFacturar)}</div>` : `<div style="font-size:10px;color:#047857;margin-top:2px;">Facturado</div>`}
        </td>
        <td style="padding:8px 10px;min-width:130px;">
          ${s.facturas.length === 0
            ? `<div style="font-size:10.5px;color:${C.muted};">Sin factura</div>`
            : `<div style="font-size:11px;color:${C.text};"><b>${s.facturasPagadas}</b> / ${s.facturas.length} pagadas</div>
               ${s.saldoCobro > 0.5
                 ? `<div style="font-size:10.5px;color:#0e7490;font-weight:700;">Saldo ${fmtCOP(s.saldoCobro)}</div>`
                 : `<div style="font-size:10px;color:#047857;">Cobrado</div>`}
               ${s.vencidas ? `<div style="font-size:9.5px;color:#991b1b;font-weight:700;">⚠️ ${s.vencidas} vencida${s.vencidas === 1 ? "" : "s"}</div>` : ""}
               <div style="font-size:9.5px;color:${C.muted};">${s.facturas.map(f => escapeHTML(f.numero || "")).join(" · ")}</div>`}
        </td>
        <td style="padding:8px 10px;min-width:170px;">
          ${proveedores.length
            ? proveedores.map(t => `<div style="font-size:10px;color:${C.text};">${t}</div>`).join("")
            : `<div style="font-size:10.5px;color:${C.muted};">Sin compras ni terceros</div>`}
          ${saldoProv > 0.5
            ? `<div style="font-size:10.5px;color:#7c3aed;font-weight:700;margin-top:2px;">Falta pagar ${fmtCOP(saldoProv)}</div>`
            : (proveedores.length ? `<div style="font-size:10px;color:#047857;margin-top:2px;">${s.proveedoresOk ? "Al día" : "Pendiente de cerrar"}</div>` : "")}
        </td>
        <td style="padding:8px 10px;text-align:right;white-space:nowrap;">
          <div style="font-weight:800;color:${margenColor};font-size:13px;">${s.margen == null ? "—" : s.margen.toFixed(1) + "%"}</div>
          <div style="font-size:9.5px;color:${C.muted};">inicial ${s.margenInicial == null ? "—" : s.margenInicial.toFixed(1) + "%"}</div>
          ${s.utilidad != null ? `<div style="font-size:9.5px;color:${C.muted};">${fmtCOP(s.utilidad)}</div>` : ""}
        </td>
        <td style="padding:8px 10px;text-align:center;">${chipEtapa(s.etapa)}</td>
        <td style="padding:8px 6px;white-space:nowrap;text-align:right;">
          <button data-action="ver-liquidacion" data-id="${op.id}" title="Liquidación de costos y margen" style="${btnStyle(C.purple, null, true)}">📊</button>
          <button data-action="control-op-detalle" data-id="${op.id}" title="Trazabilidad completa de la orden" style="${btnStyle("#4C5FA8", null, true)}">🔗</button>
          ${s.etapa === "por_facturar" || s.etapa === "por_entregar"
            ? `<button data-action="facturar-op" data-id="${op.id}" title="Facturar entregas o cantidades de esta orden" style="${btnStyle("#10b981", null, true)}">💵</button>` : ""}
        </td>
      </tr>`;
  }).join("");

  return `
    <div>
      <div style="display:flex;justify-content:space-between;align-items:flex-start;flex-wrap:wrap;gap:9px;margin-bottom:10px;">
        <div>
          <h2 style="margin:0;font-size:17px;font-weight:800;color:#2B4A82;">🎯 Control de órdenes</h2>
          <div style="font-size:11.5px;color:${C.muted};margin-top:2px;">
            Cada orden de producción por su hilo completo: entrega → factura → cobro, y lo que se les debe a proveedores y terceros. Una orden se cierra sola cuando todo eso está al día.
          </div>
        </div>
      </div>

      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));gap:8px;margin-bottom:10px;">
        ${tarjeta("ÓRDENES ABIERTAS", kpi.abiertas, "#2B4A82", `${cuentaPor.cerrada || 0} cerrada${(cuentaPor.cerrada || 0) === 1 ? "" : "s"}`)}
        ${tarjeta("POR ENTREGAR", fmtCOP(kpi.porEntregar), "#b45309", `${ordenes(cuentaPor.por_entregar || 0)} en ese paso`)}
        ${tarjeta("POR FACTURAR", fmtCOP(kpi.porFacturar), "#1d4ed8", `${ordenes(cuentaPor.por_facturar || 0)} en ese paso`)}
        ${tarjeta("POR COBRAR", fmtCOP(kpi.porCobrar), "#0e7490", kpi.vencidas ? `⚠️ ${kpi.vencidas} factura${kpi.vencidas === 1 ? "" : "s"} vencida${kpi.vencidas === 1 ? "" : "s"}` : "sin vencidas")}
        ${tarjeta("FALTA PAGAR", fmtCOP(kpi.porPagar), "#7c3aed", "proveedores y terceros")}
      </div>

      ${searchBox("search-controlop", state.searches.controlop || "", "Buscar por número de orden o cliente…", "#2B4A82")}

      <div style="display:flex;gap:6px;flex-wrap:wrap;margin-bottom:10px;">
        ${FILTROS.map(([id, label]) => `
          <button data-action="control-op-filtro" data-f="${id}"
            style="padding:5px 11px;border-radius:14px;border:1px solid ${filtro === id ? "#2B4A82" : C.border};background:${filtro === id ? "#2B4A82" : "white"};color:${filtro === id ? "white" : C.text};font-size:11px;font-weight:700;cursor:pointer;font-family:inherit;">
            ${label}${id !== "todas" && id !== "abiertas" && id !== "cerradas" && cuentaPor[id] ? ` <span style="opacity:.75;">${cuentaPor[id]}</span>` : ""}
          </button>`).join("")}
      </div>

      ${seguimientos.length === 0
        ? `<div style="padding:26px;text-align:center;background:white;border:2px dashed ${C.border};border-radius:6px;color:${C.muted};font-size:12.5px;">
             ${filtro === "abiertas" ? "🎉 No hay órdenes abiertas: todo entregado, facturado, cobrado y pagado." : "Ninguna orden en este filtro."}
           </div>`
        : `<div style="overflow-x:auto;background:white;border:1px solid ${C.border};border-radius:8px;">
             <table style="width:100%;border-collapse:collapse;font-size:11.5px;">
               <thead>
                 <tr style="background:#f8fafc;">
                   <th style="padding:7px 10px;text-align:left;font-size:10.5px;color:${C.muted};">Orden</th>
                   <th style="padding:7px 10px;text-align:left;font-size:10.5px;color:${C.muted};">Cliente · Venta</th>
                   <th style="padding:7px 10px;text-align:left;font-size:10.5px;color:${C.muted};">Entrega</th>
                   <th style="padding:7px 10px;text-align:left;font-size:10.5px;color:${C.muted};">Facturación</th>
                   <th style="padding:7px 10px;text-align:left;font-size:10.5px;color:${C.muted};">Cobro</th>
                   <th style="padding:7px 10px;text-align:left;font-size:10.5px;color:${C.muted};">Proveedores y terceros</th>
                   <th style="padding:7px 10px;text-align:right;font-size:10.5px;color:${C.muted};">Margen</th>
                   <th style="padding:7px 10px;text-align:center;font-size:10.5px;color:${C.muted};">Etapa</th>
                   <th></th>
                 </tr>
               </thead>
               <tbody>${filas}</tbody>
             </table>
           </div>`}
    </div>`;
}

registerActions({
  "control-op-filtro": (d) => {
    if (!state.controlOP) state.controlOP = {};
    state.controlOP.filtro = d.f || "abiertas";
    render();
  },
  // La trazabilidad completa vive en Reportes; desde aquí se abre directo.
  "control-op-detalle": (d) => {
    state.reporteDetalleOP = d.id;
    state.page = "reportes";
    render();
  }
});
